import Handlers from '@/scenes/engine/handlers'
import { StepType } from '@reactour/tour'
import { Dispatch } from 'react'

export const BUBBLE_TUTORIAL_WIDTH_STEP_0 = 280
export const BUBBLE_TUTORIAL_WIDTH_STEP_1 = 347
export const BUBBLE_TUTORIAL_WIDTH_STEP_2 = 349
export const BUBBLE_TUTORIAL_ARROW = 12
export const PADDING_BETWEEN_SPOTLIGHT_AND_ARROW = 15
export const BUBBLE_TUTORIAL_HEIGHT = 124
interface TourState {
  currentStep: number
  setIsOpen: Dispatch<React.SetStateAction<boolean>>
  isOpen: boolean
  steps: StepType[]
  setSteps: Dispatch<React.SetStateAction<StepType[]>>
  setCurrentStep: Dispatch<React.SetStateAction<number>>
  isHandleUndo: boolean,
  canvasStateBeforeTutorial: any
}

let tourState: TourState = {
  currentStep: 0,
  setIsOpen: () => {},
  isOpen: false,
  steps: [],
  setSteps: () => {},
  setCurrentStep: () => {},
  isHandleUndo: false,
  canvasStateBeforeTutorial: null
}

export const getTourState = (): TourState => {
  return tourState
}

export const setTourState = (newState: Partial<TourState>) => {
  tourState = { ...tourState, ...newState }
}

export const setShowedRemoveObjectTutorial = () => {
  const currentPath = window.location.pathname
  const value = {
    isOpened: true,
    from: currentPath,
  }
  localStorage.setItem('showedRemoveObjectTutorial', JSON.stringify(value))
}


export const clearBrushAnimation = () => {
  const brushAnimation = document.querySelector('.brush-animation') as HTMLElement
  if (brushAnimation) {
    const rootElement = document.querySelector('#root') as HTMLElement
    rootElement.removeChild(brushAnimation)
  }
}

export const clearTutorial = async (handler: Handlers, fromCreatePage = false): Promise<void> => {
  if(!tourState.isOpen) { return Promise.resolve() }
  tourState.isOpen = false
  if(fromCreatePage) { return Promise.resolve() }
  await handler.transactionHandler.loadCanvasJSON(tourState.canvasStateBeforeTutorial, handler.transactionHandler.isFlushed)
  await handler.pagesHandler.updateScenesPreview()
  tourState.isHandleUndo = false
}

export const createBrushTutorialAnimation = () => {
  const currentBrushAnimation = document.querySelector('.brush-animation') as HTMLElement
  const rootElement = document.querySelector('#root') as HTMLElement
  if (currentBrushAnimation) {
    rootElement.removeChild(currentBrushAnimation)
  }
  const frameElement = document.querySelector(
    '#wrap-canvas-pixel-manipulation .pixel-manipulation-container-class'
  ) as HTMLElement
  const customCursor = document.querySelector('.custom-cursor') as HTMLElement

  const frameBB = frameElement.getBoundingClientRect()

  const brushAnimation = document.createElement('div')
  brushAnimation.classList.add('brush-animation')

  brushAnimation.style.position = 'absolute'
  const originalLeft = frameBB.left + frameBB.width * 0.1 // 10% from left
  brushAnimation.style.left = `${originalLeft}px`
  brushAnimation.style.top = `${frameBB.top + frameBB.height * 0.3}px` // 35% from top

  brushAnimation.style.width = customCursor.style.width
  brushAnimation.style.height = customCursor.style.height
  brushAnimation.style.borderRadius = '50%'
  brushAnimation.style.zIndex = '2' // same with pixel manipulation canvas
  brushAnimation.style.backgroundColor = 'rgba(255,5,96, 0.8)'
  brushAnimation.style.pointerEvents = 'none'

  const brushAnimationKeyframes = `
      @keyframes brushAnimation {
          0% {
              left: ${originalLeft}px;
          }
          50% {
              left: ${originalLeft + frameBB.width * 0.6}px;
          }
          100% {
              left: ${originalLeft}px;
          }
      }
  `
  const styleSheet = document.createElement('style')
  styleSheet.innerText = brushAnimationKeyframes
  document.head.appendChild(styleSheet)
  rootElement.appendChild(brushAnimation)
  setTimeout(() => {
    brushAnimation.style.animation = 'brushAnimation 2s infinite'
  })
}

export const handleTutorialResize = () => {
  if (!tourState.isOpen) { return }
  if (tourState.currentStep === 0) {
    handleResizeStep0()
    return
  }

  reopenTour(() => {
    handleResizeStep1()
    handleResizeStep2()
  })
}

const reopenTour = (fn: () => void) => {
  const tourProvider = getTourStateElement()
  tourState.setIsOpen(false)
  fn()
  tourProvider.style.opacity = '1'
  tourState.setSteps(tourState.steps)
  tourState.setCurrentStep(tourState.currentStep)
  tourState.setIsOpen(true)
}

const handleResizeStep0 = () => {
  const spotlightElement = document.getElementById('remove-object-btn') as HTMLElement
  const spotlightBB = spotlightElement.getBoundingClientRect()
  const left = spotlightBB.left - (BUBBLE_TUTORIAL_WIDTH_STEP_0 - spotlightBB.width) / 2
  const top = spotlightBB.top + spotlightBB.height + 15
  updateBubblePosition(left, top)
}

const updateBubblePosition = (left: number, top: number) => {
  tourState.setIsOpen(false)
  tourState.steps[tourState.currentStep] = {
    ...tourState.steps[tourState.currentStep],
    position: [left, top],
  }
  tourState.setSteps(tourState.steps)
  tourState.setIsOpen(true)
}

const getPixelManipulationContainerRect = () => {
  const el = getPixelManipulationCanvasContainer()
  return el.getBoundingClientRect()
}

const getBubblePlacementForWidth = (stepWidth: number): { position: [number, number], styleKeyMap: Record<string, string> } => {
  const rect = getPixelManipulationContainerRect()
  const baseTop = rect.top + rect.height / 4

  if (!isRightPaddingValid(stepWidth)) {
    return {
      position: [
        rect.left - BUBBLE_TUTORIAL_ARROW - stepWidth - PADDING_BETWEEN_SPOTLIGHT_AND_ARROW,
        baseTop
      ],
      styleKeyMap: { right: 'left', borderRight: 'borderLeft' }
    }
  }

  return {
    position: [
      rect.left + rect.width + PADDING_BETWEEN_SPOTLIGHT_AND_ARROW,
      baseTop
    ],
    styleKeyMap: { left: 'right', borderLeft: 'borderRight' }
  }
}

const updateBubbleForStepWidth = (stepWidth: number) => {
  const { position, styleKeyMap } = getBubblePlacementForWidth(stepWidth)
  updateBubbleStyle('bubble-tutorial-arrow', styleKeyMap, position)
}

const handleResizeStep1 = () => {
  if(tourState.currentStep !== 1) { return }
  createBrushTutorialAnimation()
  updateBubbleForStepWidth(BUBBLE_TUTORIAL_WIDTH_STEP_1)
}

const handleResizeStep2 = () => {
  if(tourState.currentStep !== 2) { return }
  updateBubbleForStepWidth(BUBBLE_TUTORIAL_WIDTH_STEP_2)
}

export const getBubbleArrowBaseStyle = () => {
  return {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    borderTop: '12px solid transparent',
    borderBottom: '12px solid transparent'
  }
}

export const computeBubbleStyleAndPosition = (containerBB: DOMRect, stepWidth: number): { bubbleStyle: Record<string, any>, position: [number, number] } => {
  const baseStyle = getBubbleArrowBaseStyle()
  const baseTop = containerBB.top + containerBB.height / 4

  if (isRightPaddingValid(stepWidth)) {
    return {
      bubbleStyle: {
        ...baseStyle,
        right: 'calc(100% + 23px)',
        borderRight: '12px solid #fff'
      },
      position: [containerBB.left + containerBB.width + PADDING_BETWEEN_SPOTLIGHT_AND_ARROW, baseTop]
    }
  }

  return {
    bubbleStyle: {
      ...baseStyle,
      left: 'calc(100% + 23px)',
      borderLeft: '12px solid #fff'
    },
    position: [containerBB.left - BUBBLE_TUTORIAL_ARROW - stepWidth - PADDING_BETWEEN_SPOTLIGHT_AND_ARROW, baseTop]
  }
}
type Step = {
  selector: string;
  content?: any;
  position?: number[];
  styles?: Record<string, any>;
};

const updateBubbleStyle = (childId: string, styleKeyMap: Record<string, string>, newPosition: number[]) => {
  const steps: Step[] = tourState.steps as Step[]
  let step = tourState.steps[tourState.currentStep] as Step
  if (!step?.content?.props?.children) return steps;

  const children = step.content.props.children.map((child: any) => {
    if (!child?.props || child.props.id !== childId) return child;

    const oldStyle = child.props.style || {};
    const newStyle = { ...oldStyle };

    // rename style keys based on map: { oldKey: newKey }
    Object.entries(styleKeyMap).forEach(([from, to]) => {
      if (from in newStyle) {
        newStyle[to] = newStyle[from];
        delete newStyle[from];
      }
    });

    return {
      ...child,
      props: {
        ...child.props,
        style: newStyle,
      },
    };
  });

  step = {
    ...step,
    position: newPosition,
    content: {
      ...step.content,
      props: {
        ...step.content.props,
        children,
      },
    },
  };
  steps[tourState.currentStep] = step
}

export const handleCurrentState = (handlers, isCreatePage) => {
  if(isCreatePage) {
    return
  }
  const canvasJSON = handlers.canvasHandler.exportToCanvasJSON()
  const activeObject = handlers.canvasHandler.canvas.getActiveObject()
  //@ts-ignore
  setTourState({isHandleUndo: true, canvasStateBeforeTutorial: {canvasJSON, activeObjId: activeObject ? activeObject.id : null}})
}

const getTourStateElement = () => {
  return document.querySelector('.tour-provider') as HTMLElement
}

const getPixelManipulationCanvasContainer = () => {
  return document.querySelector('#wrap-canvas-pixel-manipulation .pixel-manipulation-container-class') as HTMLElement
}

export const isRightPaddingValid = (targetWidth: number): boolean => {
  const pixelManipulationCanvasContainer = getPixelManipulationCanvasContainer()
  const pixelManipulationCanvasContainerBB = pixelManipulationCanvasContainer.getBoundingClientRect()
  const rightPadding = window.innerWidth - (pixelManipulationCanvasContainerBB.left + pixelManipulationCanvasContainerBB.width + PADDING_BETWEEN_SPOTLIGHT_AND_ARROW * 2)
  return rightPadding > targetWidth
}